import { toastr } from 'react-redux-toastr';
import moment from 'moment';
import { makeApiCall } from '../../../services/apiService/apiService';
import {
  isEffectiveGroundDeps,
  isSamKam,
  isMasterUser,
  getVasUserRole
} from './vasExpenseAuthUtils';
import { jsonToCSVConvertor } from '../../../services/helpers';
import {
  VAS_CHARGE_TYPES,
  CHARGE_BASIS_LABELS,
  getDisplayApprovedAmount
} from './vasExpenseHelper';

export const vasExpenseActions = {
  SET_LOADING: 'SET_VAS_EXPENSE_LOADING',
  SET_LIST_DATA: 'SET_VAS_EXPENSE_LIST_DATA',
  SET_ADDRESS_DETAILS: 'SET_VAS_EXPENSE_ADDRESS_DETAILS',
  SET_TOTAL_CHARGE: 'SET_VAS_EXPENSE_TOTAL_CHARGE',
  UPDATE_PAGE_NUMBER: 'UPDATE_VAS_EXPENSE_PAGE_NUMBER',
  RESET_FORM_DATA: 'RESET_VAS_EXPENSE_FORM_DATA',
  SET_UPLOAD_URLS: 'SET_VAS_EXPENSE_UPLOAD_URLS',
  SET_CN_SUMMARY: 'SET_VAS_EXPENSE_CN_SUMMARY',
  UPDATE_FORM_ADDRESSES: 'UPDATE_VAS_EXPENSE_FORM_ADDRESSES'
};

export const setLoading = payload => ({
  type: vasExpenseActions.SET_LOADING,
  payload
});

export const setListData = payload => ({
  type: vasExpenseActions.SET_LIST_DATA,
  payload
});

export const setAddressDetails = payload => ({
  type: vasExpenseActions.SET_ADDRESS_DETAILS,
  payload
});

export const setCnSummary = payload => ({
  type: vasExpenseActions.SET_CN_SUMMARY,
  payload
});

export const updatePageNumber = payload => ({
  type: vasExpenseActions.UPDATE_PAGE_NUMBER,
  payload
});

export const resetFormData = () => ({
  type: vasExpenseActions.RESET_FORM_DATA
});

export const setUploadUrls = payload => ({
  type: vasExpenseActions.SET_UPLOAD_URLS,
  payload
});

export const updateFormAddresses = payload => ({
  type: vasExpenseActions.UPDATE_FORM_ADDRESSES,
  payload
});

// GET /vas/expense/fetch-list
export const fetchVasExpenseList = (
  filters = {},
  pageNumber = 0,
  pageSize = 10
) => (dispatch, getState) => {
  dispatch(setLoading(true));
  const { userProfile } = getState();
  const authorities = (userProfile && userProfile.authorities) || [];
  // Effective role only: a MASTER_USER / SAM_KAM who also holds ROLE_VAS_ADD
  // is not treated as ground deps
  const groundDeps = isEffectiveGroundDeps(authorities);

  const replaceParamsArr = [
    { key: 'pageNumber', val: pageNumber },
    { key: 'pageSize', val: pageSize }
  ];

  if (filters.chargeType && filters.chargeType !== 'ALL')
    replaceParamsArr.push({ key: 'chargeType', val: filters.chargeType });
  if (filters.status && filters.status !== 'ALL')
    replaceParamsArr.push({ key: 'status', val: filters.status });
  if (filters.search)
    replaceParamsArr.push({ key: 'search', val: filters.search });
  if (filters.startDate)
    replaceParamsArr.push({ key: 'startDate', val: filters.startDate });
  if (filters.endDate)
    replaceParamsArr.push({ key: 'endDate', val: filters.endDate });

  // ouCode only used/passed for GROUND_DEPS role; ignored/not passed for SAM_KAM
  if (groundDeps && filters.ouCode) {
    replaceParamsArr.push({ key: 'ouCode', val: filters.ouCode });
  }

  // clientId and rpUserId used/passed only for SAM_KAM role
  if (!groundDeps && (isSamKam(authorities) || isMasterUser(authorities))) {
    if (filters.clientId)
      replaceParamsArr.push({ key: 'clientId', val: filters.clientId });
    if (filters.rpId)
      replaceParamsArr.push({ key: 'rpUserId', val: filters.rpId });
  }

  const requestObj = {
    baseUrl: 'zoomApiUrl',
    apiEndPoint: 'vasChargesGetDashboard',
    method: 'GET',
    replaceParamsArr,
    successCb: res => {
      if (res.status === 'SUCCESS' && res.payload) {
        dispatch(
          setListData({
            dashBoardData: res.payload,
            totalElements: res.totalElements || 0,
            pageNumber,
            pageSize
          })
        );
      } else {
        dispatch(setLoading(false));
      }
    },
    failureCb: () => {
      dispatch(setLoading(false));
    },
    catchCb: () => {
      dispatch(setLoading(false));
    }
  };

  return makeApiCall(requestObj);
};

// GET /sam-kam/mappings
export const fetchSamKamMappings = callback => () => {
  const requestObj = {
    baseUrl: 'zoomApiUrl',
    apiEndPoint: 'samKamMappings',
    method: 'GET',
    successCb: res => {
      if (res.status === 'SUCCESS' && res.payload) {
        if (callback) callback(res.payload);
      }
    },
    failureCb: () => {},
    catchCb: () => {}
  };

  return makeApiCall(requestObj);
};

// GET /master/client/id-to-code-map
export const fetchClientIdMap = callback => () => {
  const requestObj = {
    baseUrl: 'zoomApiUrl',
    apiEndPoint: 'getClientIdToCodeMap',
    method: 'GET',
    successCb: res => {
      if (res.status === 'SUCCESS' && res.payload) {
        let mapData = {};
        if (res.payload.clientIdToCodeMap) {
          mapData = res.payload.clientIdToCodeMap;
        } else if (
          res.payload.clientCodes &&
          Array.isArray(res.payload.clientCodes)
        ) {
          res.payload.clientCodes.forEach(item => {
            const val =
              typeof item === 'object' && item
                ? item.clientCode || item.code || item.value || ''
                : item;
            const idVal =
              typeof item === 'object' && item
                ? item.id || item.clientId || val
                : val;
            if (idVal && val) {
              mapData[String(idVal).trim()] = String(val).trim();
            }
          });
        } else if (res.payload.codes && Array.isArray(res.payload.codes)) {
          res.payload.codes.forEach(item => {
            const val =
              typeof item === 'object' && item
                ? item.clientCode || item.code || item.value || ''
                : item;
            const idVal =
              typeof item === 'object' && item
                ? item.id || item.clientId || val
                : val;
            if (idVal && val) {
              mapData[String(idVal).trim()] = String(val).trim();
            }
          });
        } else if (Array.isArray(res.payload)) {
          res.payload.forEach(item => {
            const val =
              typeof item === 'object' && item
                ? item.clientCode || item.code || item.value || ''
                : item;
            const idVal =
              typeof item === 'object' && item
                ? item.id || item.clientId || val
                : val;
            if (idVal && val) {
              mapData[String(idVal).trim()] = String(val).trim();
            }
          });
        } else if (
          typeof res.payload === 'object' &&
          !Array.isArray(res.payload)
        ) {
          mapData = res.payload;
        }
        if (callback) callback(mapData);
      }
    },
    failureCb: () => {},
    catchCb: () => {}
  };

  return makeApiCall(requestObj);
};

// GET /vas/expense/details
export const fetchAddressVehicleDetails = (params, callback) => dispatch => {
  const requestObj = {
    baseUrl: 'zoomApiUrl',
    apiEndPoint: 'vasUnionDetails',
    method: 'GET',
    replaceParamsArr: [
      { key: 'entityType', val: params.entityType },
      { key: 'entityId', val: params.entityId },
      { key: 'locationType', val: params.locationType },
      { key: 'activityType', val: params.activityType || '' },
      { key: 'vasChargeType', val: params.vasChargeType || '' }
    ],
    successCb: res => {
      if (res.status === 'SUCCESS' && res.payload) {
        const rawAddresses = res.payload.addressDTOList || [];
        const formattedAddresses = rawAddresses.map(addr => ({
          ...addr,
          allCnotes: addr.allCnotes || [...(addr.cnoteList || [])]
        }));
        dispatch(
          setAddressDetails({
            addresses: formattedAddresses,
            vehicleNumber: res.payload.vehicleNumber || null,
            vehicleType: res.payload.vehicleType || null
          })
        );
        if (callback) callback(res.payload);
      } else if (callback) {
        callback(null);
      }
    },
    failureCb: () => {
      if (callback) callback(null);
    },
    catchCb: () => {
      if (callback) callback(null);
    }
  };

  return makeApiCall(requestObj);
};

// GET /vas/expense/cn-summary
export const fetchCnSummary = params => dispatch => {
  if (!params.cnoteList || !params.cnoteList.length) {
    return null;
  }
  const requestObj = {
    baseUrl: 'zoomApiUrl',
    apiEndPoint: 'getCnSummary',
    method: 'GET',
    replaceParamsArr: [{ key: 'cnoteList', val: params.cnoteList.join(',') }],
    successCb: res => {
      if (res.status === 'SUCCESS') {
        dispatch(setCnSummary(res.payload));
      }
    },
    failureCb: () => {},
    catchCb: () => {}
  };

  return makeApiCall(requestObj);
};

export const updateAddressesAndSummary = (addresses, cnoteList) => dispatch => {
  dispatch(updateFormAddresses(addresses));
  if (cnoteList && cnoteList.length > 0) {
    dispatch(fetchCnSummary({ cnoteList }));
  } else {
    dispatch(setCnSummary(null));
  }
};

// POST /vas/expense/upsert
export const submitVasExpense = (
  data,
  successCallback,
  failureCallback
) => () => {
  const requestObj = {
    baseUrl: 'zoomApiUrl',
    apiEndPoint: 'postVasData',
    method: 'POST',
    data,
    successCb: res => {
      if (res.status === 'SUCCESS') {
        toastr.success('Success', 'VAS request submitted successfully');
        if (successCallback) successCallback(res);
      } else {
        if (failureCallback) failureCallback(res);
      }
    },
    failureCb: res => {
      if (failureCallback) failureCallback(res);
    },
    catchCb: err => {
      if (failureCallback) failureCallback(err);
    }
  };

  return makeApiCall(requestObj);
};

// File Uploads
export const uploadFile = (
  file,
  isReceiptFile,
  successCallback,
  failureCallback
) => dispatch => {
  if (!file) return null;

  const formData = new FormData();
  formData.append(isReceiptFile ? 'receipt' : 'emailEvidence', file, file.name);

  const requestObj = {
    baseUrl: 'zoomApiUrl',
    apiEndPoint: isReceiptFile ? 'uploadVasReceipt' : 'uploadVasEmail',
    postFormData: true,
    data: formData,
    method: 'POST',
    successCb: res => {
      if (res.status === 'SUCCESS' && res.payload) {
        if (isReceiptFile) {
          dispatch(setUploadUrls({ receiptUrl: res.payload }));
        } else {
          dispatch(setUploadUrls({ emailEvidenceUrl: res.payload }));
        }
        if (successCallback) successCallback(res.payload);
      } else {
        if (failureCallback) failureCallback(res);
      }
    },
    failureCb: res => {
      if (failureCallback) failureCallback(res);
    },
    catchCb: err => {
      if (failureCallback) failureCallback(err);
    }
  };

  return makeApiCall(requestObj);
};

// POST /vas/expense/{id}/approve (multipart/form-data)
export const approveVasExpense = (
  { id, approvedAmount, confirmationFile },
  successCallback,
  failureCallback
) => () => {
  const formData = new FormData();
  formData.append('approvedAmount', approvedAmount);
  // confirmationFile is optional for HIGHER_FLOOR approvals.
  if (confirmationFile) {
    formData.append(
      'confirmationFile',
      confirmationFile,
      confirmationFile.name
    );
  }

  const requestObj = {
    baseUrl: 'zoomApiUrl',
    apiEndPoint: 'approveVasExpense',
    pathVariables: [{ key: '{id}', val: id }],
    postFormData: true,
    data: formData,
    method: 'POST',
    successCb: res => {
      if (res.status === 'SUCCESS') {
        toastr.success('Success', 'VAS request approved successfully');
        if (successCallback) successCallback();
      } else {
        if (failureCallback) failureCallback(res);
      }
    },
    failureCb: res => {
      if (failureCallback) failureCallback(res);
    },
    catchCb: err => {
      if (failureCallback) failureCallback(err);
    }
  };

  return makeApiCall(requestObj);
};

// POST /vas/expense/{id}/reject (multipart/form-data)
// T37678: the reject endpoint is now multipart so a supporting document can be attached (mandatory
// for HIGHER_FLOOR). reason + optional rejectionAttachment go in the form body.
export const rejectVasExpense = (
  { id, reason, rejectionAttachment },
  successCallback,
  failureCallback
) => () => {
  const formData = new FormData();
  if (reason) {
    formData.append('reason', reason);
  }
  if (rejectionAttachment) {
    formData.append(
      'rejectionAttachment',
      rejectionAttachment,
      rejectionAttachment.name
    );
  }

  const requestObj = {
    baseUrl: 'zoomApiUrl',
    apiEndPoint: 'rejectVasExpense',
    pathVariables: [{ key: '{id}', val: id }],
    postFormData: true,
    data: formData,
    method: 'POST',
    successCb: res => {
      if (res.status === 'SUCCESS') {
        toastr.success('Success', 'VAS request rejected successfully');
        if (successCallback) successCallback();
      } else {
        if (failureCallback) failureCallback(res);
      }
    },
    failureCb: res => {
      if (failureCallback) failureCallback(res);
    },
    catchCb: err => {
      if (failureCallback) failureCallback(err);
    }
  };

  return makeApiCall(requestObj);
};

// POST /vas/expense/{id}/revoke
export const revokeVasExpense = (
  id,
  successCallback,
  failureCallback
) => () => {
  const requestObj = {
    baseUrl: 'zoomApiUrl',
    apiEndPoint: 'revokeVasExpense',
    pathVariables: [{ key: '{id}', val: id }],
    method: 'POST',
    successCb: res => {
      if (res.status === 'SUCCESS') {
        toastr.success('Success', 'VAS request revoked successfully');
        if (successCallback) successCallback();
      } else {
        if (failureCallback) failureCallback(res);
      }
    },
    failureCb: res => {
      if (failureCallback) failureCallback(res);
    },
    catchCb: err => {
      if (failureCallback) failureCallback(err);
    }
  };

  return makeApiCall(requestObj);
};

// GET /vas/expense/fetch-list (for CSV download of full data)
export const downloadVasExpenseList = (filters = {}, totalElements = 10000) => (
  dispatch,
  getState
) => {
  dispatch(setLoading(true));
  const { userProfile } = getState();
  const authorities = (userProfile && userProfile.authorities) || [];
  // Effective role only: a MASTER_USER / SAM_KAM who also holds ROLE_VAS_ADD
  // is not treated as ground deps
  const groundDeps = isEffectiveGroundDeps(authorities);

  const replaceParamsArr = [
    { key: 'pageNumber', val: 0 },
    { key: 'pageSize', val: totalElements || 10000 }
  ];

  if (filters.chargeType && filters.chargeType !== 'ALL')
    replaceParamsArr.push({ key: 'chargeType', val: filters.chargeType });
  if (filters.status && filters.status !== 'ALL')
    replaceParamsArr.push({ key: 'status', val: filters.status });
  if (filters.search)
    replaceParamsArr.push({ key: 'search', val: filters.search });
  if (filters.startDate)
    replaceParamsArr.push({ key: 'startDate', val: filters.startDate });
  if (filters.endDate)
    replaceParamsArr.push({ key: 'endDate', val: filters.endDate });

  if (groundDeps && filters.ouCode) {
    replaceParamsArr.push({ key: 'ouCode', val: filters.ouCode });
  }

  if (!groundDeps && (isSamKam(authorities) || isMasterUser(authorities))) {
    if (filters.clientId)
      replaceParamsArr.push({ key: 'clientId', val: filters.clientId });
    if (filters.rpId)
      replaceParamsArr.push({ key: 'rpUserId', val: filters.rpId });
  }

  const requestObj = {
    baseUrl: 'zoomApiUrl',
    apiEndPoint: 'vasChargesGetDashboard',
    method: 'GET',
    replaceParamsArr,
    successCb: res => {
      dispatch(setLoading(false));
      if (res.status === 'SUCCESS' && res.payload) {
        const data = res.payload || [];
        const csvColumns = [
          { Header: 'ID', formatter: row => row.id || '' },
          {
            Header: 'Date',
            formatter: row =>
              row.receiptDate
                ? moment(row.receiptDate).format('YYYY-MM-DD')
                : '-'
          },
          { Header: 'Raised By', formatter: row => row.raisedByName || '-' },
          {
            Header: 'Location Type',
            formatter: row => row.chargeLocationType || '-'
          },
          {
            Header: 'Activity Type',
            formatter: row => row.activityType || '-'
          },
          { Header: 'Location/OU Code', formatter: row => row.ouCode || '-' },
          {
            Header: 'Address',
            formatter: row =>
              (row.addressDTOList &&
                row.addressDTOList[0] &&
                row.addressDTOList[0].address) ||
              '-'
          },
          {
            Header: 'Pincode',
            formatter: row =>
              (row.addressDTOList &&
                row.addressDTOList[0] &&
                row.addressDTOList[0].pincode) ||
              '-'
          },
          { Header: 'Entity Type', formatter: row => row.entityType || '-' },
          { Header: 'Entity ID', formatter: row => row.entityId || '-' },
          {
            Header: 'CNs',
            formatter: row => (row.applicableCnotes || []).join('; ')
          },
          { Header: 'Client Code', formatter: row => row.clientCode || '-' },
          { Header: 'Client Name', formatter: row => row.clientName || '-' },
          { Header: 'RP Code', formatter: row => row.rpCode || '-' },
          { Header: 'RP Name', formatter: row => row.rpName || '-' },
          {
            Header: 'Charge Type',
            formatter: row =>
              VAS_CHARGE_TYPES[row.chargeType] || row.chargeType || '-'
          },
          {
            Header: 'Charge Basis',
            formatter: row =>
              CHARGE_BASIS_LABELS[row.chargeBasis] || row.chargeBasis || '-'
          },
          {
            Header: 'Rate/Lumpsum',
            formatter: row => row.chargedAmount || '-'
          },
          { Header: 'Total Amount', formatter: row => row.totalAmount || '-' },
          {
            Header: 'Approved Amount',
            formatter: row => {
              const disp = getDisplayApprovedAmount(
                row,
                getVasUserRole(authorities)
              );
              return disp != null ? disp : '-';
            }
          },
          {
            Header: 'Last Updated',
            formatter: row =>
              row.lastUpdatedAt
                ? moment(row.lastUpdatedAt).format('YYYY-MM-DD')
                : '-'
          },
          {
            Header: 'Actioned By',
            formatter: row => row.lastActionedByName || '-'
          },
          { Header: 'Status', formatter: row => row.status || '-' },
          { Header: 'Remarks', formatter: row => row.remarks || '-' }
        ];

        jsonToCSVConvertor(data, 'VAS_Expense_Report', csvColumns);
      }
    },
    failureCb: () => {
      dispatch(setLoading(false));
    },
    catchCb: () => {
      dispatch(setLoading(false));
    }
  };

  return makeApiCall(requestObj);
};
